import { ExternalLink, FileText, Trash2 } from "lucide-react";
import PropTypes from "prop-types";
import FileUploader from "@components/ui-components/file-uploader";
import LoadingButton from "@components/ui-components/loading-button";
import {
    DOCS_EMPTY_NOTE,
    KYC_ACCEPT_ATTR,
    KYC_DROPZONE_NOTE,
    KYC_DROPZONE_TITLE,
    KYC_EXTENSIONS,
    KYC_MAX_SIZE_MB,
    KYC_MIME_TYPES,
    KYC_NAME_PLACEHOLDER,
    KYC_UPLOAD_TYPE
} from "../misc/constants";

// The brand's KYC paperwork: the picker and whatever is already attached. All
// of the staging, preview and progress behaviour belongs to <FileUploader />.
const KycDocumentsCard = ({ documents, onUploaded, onDelete, isDeleting }) => (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
        <p className="mt-0.5 text-xs text-muted">
            {documents.length > 0
                ? `${documents.length} document${documents.length > 1 ? "s" : ""} on file`
                : DOCS_EMPTY_NOTE}
        </p>

        <FileUploader
            className="mt-4"
            uploadType={KYC_UPLOAD_TYPE}
            mimeTypes={KYC_MIME_TYPES}
            extensions={KYC_EXTENSIONS}
            maxSizeMb={KYC_MAX_SIZE_MB}
            accept={KYC_ACCEPT_ATTR}
            title={KYC_DROPZONE_TITLE}
            note={KYC_DROPZONE_NOTE}
            itemNoun="document"
            requireName
            namePlaceholder={KYC_NAME_PLACEHOLDER}
            onUploaded={onUploaded}
            data-cy="kyc-uploader" />

        {documents.length > 0 && (
            <ul className="mt-4 space-y-1">
                {documents.map((doc, idx) => (
                    <li key={doc._id || `${doc.name}-${idx}`} className="flex items-center gap-2">
                        <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={doc.name}
                            className="group flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-light">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger">
                                <FileText className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">{doc.name}</span>
                            <ExternalLink className="h-4 w-4 shrink-0 text-muted opacity-0 transition-opacity group-hover:opacity-100" />
                        </a>
                        {/* Older entries may have no `_id`, and cannot be addressed for deletion. */}
                        {doc._id && (
                            <LoadingButton
                                color="neutral"
                                size="xs"
                                icon={Trash2}
                                tooltip={`Delete ${doc.name}`}
                                loading={isDeleting(doc)}
                                onClick={() => onDelete(doc)}
                                testId="delete-kyc-doc" />
                        )}
                    </li>
                ))}
            </ul>
        )}
    </section>
);

KycDocumentsCard.propTypes = {
    documents : PropTypes.array.isRequired,
    onUploaded: PropTypes.func.isRequired,
    onDelete  : PropTypes.func.isRequired,
    isDeleting: PropTypes.func.isRequired
};

export default KycDocumentsCard;
